import { useState, useEffect, useRef, useContext } from "react";
import "./index.css";
import i18n from "../../i18n";
import dashGlobe from "../../assets/shapes_svg/dashboards/dash-globe.svg";
import { Globe } from "lucide-react";
import {
  SUPPORTED_LANGUAGE_CODES,
  normalizeLanguageCode,
} from "../../constants/supportedLanguages";
import { baseUrl } from "../../apis/util";
import LanguageContext from "../../contexts/LanguageContext";

function LangNav({ embedInTopBar = false }) {
  const languageContext = useContext(LanguageContext);
  const updateLanguagePreference = languageContext?.updateLanguagePreference;
  const [langSelected, setLangSelected] = useState(
    () => normalizeLanguageCode(i18n.language) || "da"
  );
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const savedLanguage = normalizeLanguageCode(
      localStorage.getItem("preferredLanguage")
    );
    if (savedLanguage) {
      setLangSelected(savedLanguage);
      if (normalizeLanguageCode(i18n.language) !== savedLanguage) {
        i18n.changeLanguage(savedLanguage);
      }
    }
  }, []);

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      const normalized = normalizeLanguageCode(lng) || "da";
      setLangSelected(normalized);
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleLanguageChange = async (value) => {
    const normalized = normalizeLanguageCode(value) || "da";
    setIsOpen(false);

    if (updateLanguagePreference) {
      await updateLanguagePreference(normalized);
      return;
    }

    setLangSelected(normalized);
    localStorage.setItem("preferredLanguage", normalized);
    await i18n.changeLanguage(normalized);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      const response = await fetch(
        `${baseUrl}/api/accounts/language-preference/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ language: normalized }),
        }
      );
      if (!response.ok) {
        console.warn("Failed to update language preference on server");
      }
    } catch (error) {
      console.error("Error updating language preference:", error);
    }
  };

  const otherLanguages = SUPPORTED_LANGUAGE_CODES.filter(
    (code) => code !== langSelected
  );

  return (
    <div
      ref={rootRef}
      className={`langNav${embedInTopBar ? " langNav--embedTopBar" : " sticky-langnav"}`}
    >
      <button
        type="button"
        className={
          embedInTopBar ? "langNav__trigger langNav__trigger--embed" : "langNav__trigger"
        }
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Change language"
        title={langSelected.toUpperCase()}
      >
        {embedInTopBar ? (
          <Globe className="langNav__globeIcon" aria-hidden="true" />
        ) : (
          <>
            <img src={dashGlobe} alt="" width={54} height={54} />
            <span className="langNav__code">{langSelected.toUpperCase()}</span>
          </>
        )}
      </button>

      {isOpen ? (
        <div className="langNav__dropdown" role="listbox">
          <div className="langNav__dropdownCurrent" aria-current="true">
            {langSelected.toUpperCase()}
          </div>
          <div className="langNav__dropdownList">
            {otherLanguages.map((code) => (
              <button
                key={code}
                type="button"
                role="option"
                aria-selected={false}
                className="langNav__option"
                onClick={() => handleLanguageChange(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default LangNav;
